import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useUserType } from '@/hooks/useUserType';
import { supabase } from '@/integrations/supabase/client';
import NotificationBell from '@/components/notifications/NotificationBell';
import MessageInboxButton from '@/components/messaging/MessageInboxButton';
import { ChevronDown, User, Settings, LogOut, Briefcase, ShoppingBag, MessageCircle, FileText, CreditCard, BarChart3, HelpCircle, Shield, Menu, X, Camera, Heart, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [userProfile, setUserProfile] = useState<{
    id: string;
    username?: string;
    full_name?: string;
    avatar_url?: string;
    user_type?: 'customer' | 'creator';
  } | null>(null);

  const { user, signOut } = useAuth();
  const { userType, updateUserType, isCreator, isCustomer } = useUserType();
  const location = useLocation();
  const navigate = useNavigate();

  // Scroll effect for transparent-to-solid background
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fetch user profile data
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!user) return;

      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .single();

        if (error) throw error;
        setUserProfile(data);
      } catch (error) {
        console.error('Error fetching user profile:', error);
      }
    };

    fetchUserProfile();
  }, [user]);

  const handleSignOut = async () => {
    try {
      await signOut();
    } finally {
      setIsMobileMenuOpen(false);
    }
  };

  const handleRoleSwitch = async (newRole: 'customer' | 'creator') => {
    const success = await updateUserType(newRole);
    if (success) {
      if (location.pathname === '/dashboard') {
        window.location.reload();
      } else {
        navigate('/dashboard');
      }
    }
  };

  const getAvatarUrl = () => {
    return userProfile?.avatar_url ||
      'https://lh3.googleusercontent.com/aida-public/AB6AXuANVbVGAI5VwZleCEJFDATlKXJcz2yEPJyVHnyQw__ZEShtdpJcTYCirjgsD_tJvgdTgPW0V7foqJrl4n28YFYf3FrOlVP8wEa8slOAyPanrn50lFlHnNk9aMn72WyjPvjI76zNJsgU_A4DgA4PrjK-qc8dcpcnD3fT4FxOPeQhhP4n83lS7OoYHbuBsCvRlYUPYg7N06FRXVTQHe4glKxFi8POhl-wZ2Cl9ItC2CSjWtK4PNuHB0tCOTI6sW89UIxPJCiF_ehKcn5C';
  };

  const navLinkClass = "text-muted-foreground hover:text-primary transition-colors font-medium tracking-wide uppercase text-xs";

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 overflow-visible ${isScrolled ? 'bg-background/80 backdrop-blur-md border-b border-border shadow-sm py-4' : 'bg-transparent py-6'
        }`}
    >
      <div className="px-6 sm:px-16 lg:px-24">
        <div className="flex items-center justify-between max-w-screen-xl mx-auto">
          <div className="flex items-center gap-3 overflow-visible">
            <Link to={user ? "/dashboard" : "/"} className="flex items-center gap-3 -my-3">
              <img src="/logo.png" alt="Lenzura logo" className="h-[72px] w-auto" />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            {!user ? (
              <>
                <Link className={navLinkClass} to="/gigs">Browse</Link>
                <Link className={navLinkClass} to="/how-it-works">How it works</Link>
                <Link className={navLinkClass} to="/about">About</Link>
              </>
            ) : isCustomer ? (
              <>
                <Link className={navLinkClass} to="/gigs">Browse</Link>
                <Link className={navLinkClass} to="/orders">Orders</Link>
                <Link className={navLinkClass} to="/messages">Messages</Link>
              </>
            ) : (
              <>
                <Link className={navLinkClass} to="/dashboard">Dashboard</Link>
                <Link className={navLinkClass} to="/gigs/manage">Gigs</Link>
                <Link className={navLinkClass} to="/orders">Orders</Link>
                <Link className={navLinkClass} to="/messages">Messages</Link>
              </>
            )}
          </nav>

          <div className="hidden lg:flex items-center gap-4">
            {!user ? (
              <>
                <Link to="/signin">
                  <Button className="inline-flex items-center px-8 py-3 bg-transparent border-2 border-foreground/30 text-foreground/70 font-bold rounded-lg hover:bg-foreground/10 hover:text-primary transition-colors">
                    Log in
                  </Button>
                </Link>
                <Link to="/signup">
                  <Button variant="default" size="sm" className="font-bold">
                    Post a job
                  </Button>
                </Link>
              </>
            ) : (
              <div className="flex items-center gap-4">
                {/* Role Switcher */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className={`flex items-center gap-2 h-9 border transition-colors ${isCreator
                        ? 'bg-primary/10 text-primary border-primary/50 hover:bg-primary/20'
                        : 'bg-secondary/50 text-secondary-foreground border-secondary hover:bg-secondary/80'
                        }`}

                    >
                      {isCreator ? <Camera className="h-4 w-4" /> : <Briefcase className="h-4 w-4" />}
                      <span className="hidden sm:inline font-medium">{isCreator ? 'Creator Mode' : 'Customer Mode'}</span>
                      <ChevronDown className="h-3 w-3 opacity-50" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem
                      className="cursor-pointer"
                      onClick={() => handleRoleSwitch('customer')}
                    >
                      <Briefcase className="mr-2 h-4 w-4 text-muted-foreground" />
                      <span>Switch to Customer</span>
                      {isCustomer && <span className="ml-auto text-muted-foreground text-xs">Active</span>}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="cursor-pointer"
                      onClick={() => handleRoleSwitch('creator')}
                    >
                      <Camera className="mr-2 h-4 w-4 text-primary" />
                      <span>Switch to Creator</span>
                      {isCreator && <span className="ml-auto text-primary text-xs">Active</span>}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                <Link to={isCreator ? '/gigs/create' : '/gigs'}>
                  <Button
                    variant="default"
                    size="sm"
                    className="hidden sm:flex items-center gap-2 h-9 font-bold"
                  >
                    {isCreator ? <Camera className="h-3.5 w-3.5" /> : <Search className="h-3.5 w-3.5" />}
                    <span>{isCreator ? 'Create Gig' : 'Browse Gigs'}</span>
                  </Button>
                </Link>

                <div className="flex items-center gap-1">
                  <MessageInboxButton />
                  <NotificationBell />
                </div>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="relative h-9 w-9 rounded-full p-0 overflow-hidden border border-border hover:bg-accent focus:ring-0 ring-offset-0">
                      <Avatar className="h-full w-full">
                        <AvatarImage src={getAvatarUrl()} alt={userProfile?.full_name || 'User'} className="object-cover" />
                        <AvatarFallback className="bg-muted text-muted-foreground">{userProfile?.full_name?.charAt(0) || user.email?.charAt(0) || 'U'}</AvatarFallback>
                      </Avatar>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56 p-1">
                    <DropdownMenuLabel className="font-normal px-2 py-1.5">
                      <div className="flex flex-col space-y-1">
                        <p className="text-sm font-medium leading-none text-foreground">{userProfile?.full_name || 'User'}</p>
                        <p className="text-xs leading-none text-muted-foreground truncate">{user.email}</p>
                      </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator className="my-1" />
                    <DropdownMenuLabel className="px-2 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Account</DropdownMenuLabel>

                    <DropdownMenuItem asChild className="cursor-pointer rounded-sm">
                      <Link to="/profile" className="w-full flex items-center">
                        <User className="mr-2 h-4 w-4 text-muted-foreground" />
                        <span>Profile</span>
                      </Link>
                    </DropdownMenuItem>

                    <DropdownMenuItem asChild className="cursor-pointer rounded-sm">
                      <Link to="/dashboard" className="w-full flex items-center">
                        <BarChart3 className="mr-2 h-4 w-4 text-muted-foreground" />
                        <span>Dashboard</span>
                      </Link>
                    </DropdownMenuItem>

                    <DropdownMenuItem asChild className="cursor-pointer rounded-sm">
                      <Link to="/messages" className="w-full flex items-center">
                        <MessageCircle className="mr-2 h-4 w-4 text-muted-foreground" />
                        <span>Messages</span>
                      </Link>
                    </DropdownMenuItem>

                    <DropdownMenuSeparator className="my-1" />
                    <DropdownMenuLabel className="px-2 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Workspace</DropdownMenuLabel>

                    {isCreator && (
                      <>
                        <DropdownMenuItem asChild className="cursor-pointer rounded-sm">
                          <Link to="/gigs/manage" className="w-full flex items-center">
                            <Briefcase className="mr-2 h-4 w-4 text-muted-foreground" />
                            <span>My Gigs</span>
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild className="cursor-pointer rounded-sm">
                          <Link to="/analytics" className="w-full flex items-center">
                            <BarChart3 className="mr-2 h-4 w-4 text-muted-foreground" />
                            <span>Analytics</span>
                          </Link>
                        </DropdownMenuItem>
                      </>
                    )}

                    <DropdownMenuItem asChild className="cursor-pointer rounded-sm">
                      <Link to="/saved-gigs" className="w-full flex items-center">
                        <Heart className="mr-2 h-4 w-4 text-muted-foreground" />
                        <span>Saved Gigs</span>
                      </Link>
                    </DropdownMenuItem>

                    <DropdownMenuItem asChild className="cursor-pointer rounded-sm">
                      <Link to="/orders" className="w-full flex items-center">
                        <FileText className="mr-2 h-4 w-4 text-muted-foreground" />
                        <span>{isCreator ? 'My Orders' : 'Order History'}</span>
                      </Link>
                    </DropdownMenuItem>

                    <DropdownMenuSeparator className="my-1" />
                    <DropdownMenuLabel className="px-2 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Billing</DropdownMenuLabel>

                    <DropdownMenuItem asChild className="cursor-pointer rounded-sm">
                      <Link to="/dashboard/payments" className="w-full flex items-center">
                        <CreditCard className="mr-2 h-4 w-4 text-muted-foreground" />
                        <span>Wallet & Payments</span>
                      </Link>
                    </DropdownMenuItem>

                    <DropdownMenuItem asChild className="cursor-pointer rounded-sm">
                      <Link to="/dashboard/settings" className="w-full flex items-center">
                        <Settings className="mr-2 h-4 w-4 text-muted-foreground" />
                        <span>Account Settings</span>
                      </Link>
                    </DropdownMenuItem>

                    <DropdownMenuSeparator className="my-1" />
                    <DropdownMenuLabel className="px-2 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Support</DropdownMenuLabel>

                    <DropdownMenuItem asChild className="cursor-pointer rounded-sm">
                      <Link to="/help" className="w-full flex items-center">
                        <HelpCircle className="mr-2 h-4 w-4 text-muted-foreground" />
                        <span>Help Center</span>
                      </Link>
                    </DropdownMenuItem>

                    <DropdownMenuItem
                      className="cursor-pointer text-destructive focus:text-destructive rounded-sm mt-1"
                      onClick={handleSignOut}
                    >
                      <LogOut className="mr-2 h-4 w-4" />
                      <span>Log out</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="lg:hidden flex items-center gap-4">
            {user && (
              <div className="flex items-center gap-1">
                <NotificationBell />
              </div>
            )}
            <Button
              variant="ghost"
              size="icon"
              className="text-foreground hover:bg-foreground/10"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Content */}
      {isMobileMenuOpen && (
        <div className="lg:hidden fixed inset-x-0 top-[72px] bottom-0 bg-background/95 backdrop-blur-md border-t border-border p-6 z-40 overflow-y-auto animate-in slide-in-from-top-5 duration-200">
          <nav className="flex flex-col gap-6">
            {!user ? (
              <>
                <div className="flex flex-col gap-4">
                  <Link className="text-lg font-medium text-foreground hover:text-primary transition-colors" to="/gigs" onClick={() => setIsMobileMenuOpen(false)}>Browse</Link>
                  <Link className="text-lg font-medium text-foreground hover:text-primary transition-colors" to="/how-it-works" onClick={() => setIsMobileMenuOpen(false)}>How it works</Link>
                  <Link className="text-lg font-medium text-foreground hover:text-primary transition-colors" to="/about" onClick={() => setIsMobileMenuOpen(false)}>About</Link>
                </div>

                <div className="h-px bg-border w-full"></div>

                <div className="flex flex-col gap-4">
                  <Link to="/signin" onClick={() => setIsMobileMenuOpen(false)}>
                    <Button variant="secondary" className="w-full justify-center">Log in</Button>
                  </Link>
                  <Link to="/signup" onClick={() => setIsMobileMenuOpen(false)}>
                    <Button variant="default" className="w-full justify-center">Post a job</Button>
                  </Link>
                </div>
              </>
            ) : (
              <div className="flex flex-col gap-4">
                <div className="flex items-center gap-3 mb-2">
                  <Avatar className="h-10 w-10 border border-border">
                    <AvatarImage src={getAvatarUrl()} alt={userProfile?.full_name || 'User'} />
                    <AvatarFallback className="bg-muted text-muted-foreground">{userProfile?.full_name?.charAt(0) || 'U'}</AvatarFallback>
                  </Avatar>
                  <div className="flex flex-col">
                    <span className="font-medium text-foreground">{userProfile?.full_name || 'User'}</span>
                    <span className="text-xs text-muted-foreground">{user.email}</span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <Button
                    variant="outline"
                    size="sm"
                    className={isCustomer
                      ? "bg-secondary/50 text-secondary-foreground border-secondary hover:bg-secondary/80"
                      : "border-border text-muted-foreground hover:bg-accent hover:text-foreground"}
                    onClick={() => handleRoleSwitch('customer')}
                  >
                    <Briefcase className="mr-2 h-4 w-4" />
                    Customer
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className={isCreator
                      ? "bg-primary/10 text-primary border-primary/50 hover:bg-primary/20"
                      : "border-border text-muted-foreground hover:bg-accent hover:text-foreground"}
                    onClick={() => handleRoleSwitch('creator')}
                  >
                    <Camera className="mr-2 h-4 w-4" />
                    Creator
                  </Button>
                </div>

                <div className="flex flex-col gap-2">
                  {isCustomer ? (
                    <>
                      <Link to="/gigs" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center gap-3 p-2 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground transition-colors">
                        <ShoppingBag className="h-5 w-5" />
                        Browse Services
                      </Link>
                      <Link to="/orders" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center gap-3 p-2 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground transition-colors">
                        <FileText className="h-5 w-5" />
                        My Orders
                      </Link>
                    </>
                  ) : (
                    <>
                      <Link to="/dashboard" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center gap-3 p-2 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground transition-colors">
                        <BarChart3 className="h-5 w-5" />
                        Dashboard
                      </Link>
                      <Link to="/gigs/manage" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center gap-3 p-2 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground transition-colors">
                        <Briefcase className="h-5 w-5" />
                        My Gigs
                      </Link>
                      <Link to="/orders" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center gap-3 p-2 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground transition-colors">
                        <FileText className="h-5 w-5" />
                        My Orders
                      </Link>
                    </>
                  )}

                  <Link to="/messages" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center gap-3 p-2 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground transition-colors">
                    <MessageCircle className="h-5 w-5" />
                    Messages
                  </Link>

                  <Link to="/profile" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center gap-3 p-2 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground transition-colors">
                    <User className="h-5 w-5" />
                    Profile
                  </Link>

                  <Link to={isCreator ? '/gigs/create' : '/gigs'} onClick={() => setIsMobileMenuOpen(false)} className="flex items-center gap-3 p-2 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground transition-colors">
                    {isCreator ? <Camera className="h-5 w-5" /> : <Search className="h-5 w-5" />}
                    {isCreator ? 'Create Gig' : 'Browse Gigs'}
                  </Link>

                  <Link to="/dashboard/settings" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center gap-3 p-2 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground transition-colors">
                    <Settings className="h-5 w-5" />
                    Settings
                  </Link>
                </div>

                <div className="h-px bg-border w-full"></div>

                <Button variant="ghost" className="justify-start text-destructive hover:text-destructive hover:bg-destructive/10 px-2" onClick={handleSignOut}>
                  <LogOut className="mr-2 h-5 w-5" />
                  Sign out
                </Button>
              </div>
            )}
          </nav>
        </div>
      )}
    </header>
  );
};

export default Navbar;